import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AchievementTimeline = ({ user }) => {
  const [achievements, setAchievements] = useState([]);
  const [activities, setActivities] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    if (user) {
      loadUserAchievements();
    }
  }, [user]);

  const loadUserAchievements = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('session_token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Load user achievements and stats
      const achievementsResponse = await axios.get(`${API}/achievements/user`, { headers });
      
      setAchievements(achievementsResponse.data.unlocked_achievements || []);
      setActivities(achievementsResponse.data.recent_activities || []);
      setStats(achievementsResponse.data.user_stats || {});
      
    } catch (err) {
      console.error('Error loading achievements:', err);
      setError('Failed to load achievements. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      milestone: 'bg-purple-100 text-purple-800 border-purple-300',
      creative: 'bg-blue-100 text-blue-800 border-blue-300',
      engagement: 'bg-green-100 text-green-800 border-green-300',
      social: 'bg-orange-100 text-orange-800 border-orange-300',
      streak: 'bg-red-100 text-red-800 border-red-300',
    };
    return colors[category] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getActivityIcon = (activityType) => {
    const icons = {
      campaign_created: '🎯',
      image_generated: '🎨',
      video_generated: '🎬',
      persona_created: '👥',
      campaign_shared: '📤',
      login: '🔑',
    };
    return icons[activityType] || '📝';
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInSeconds = Math.floor((now - time) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  const filteredAchievements = achievements.filter(achievement => {
    if (activeFilter === 'all') return true;
    return achievement.definition?.category === activeFilter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 mb-4">⚠️</div>
        <p className="text-gray-600 dark:text-gray-400">{error}</p>
        <button
          onClick={loadUserAchievements}
          className="mt-4 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Achievement Stats Summary */}
      {stats && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">🏆 Achievement Progress</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-500">{stats.total_achievements || 0}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Achievements Unlocked</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-500">{stats.total_points || 0}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Total Points</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-500">{stats.current_streak || 0}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Current Streak</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-500">{stats.longest_streak || 0}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Best Streak</div>
            </div>
          </div>

          {/* Category Progress */}
          {stats.achievements_by_category && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {Object.entries(stats.achievements_by_category).map(([category, count]) => (
                <div key={category} className="text-center">
                  <div className={`inline-flex px-3 py-1 rounded-full text-sm font-medium border ${getCategoryColor(category)}`}>
                    {category}
                  </div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-white mt-2">{count}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {['all', 'milestone', 'creative', 'engagement', 'social', 'streak'].map((filter) => (
          <button
            key={filter}
            onClick={() => setActiveFilter(filter)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeFilter === filter
                ? 'bg-orange-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            {filter.charAt(0).toUpperCase() + filter.slice(1)}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Achievement Timeline */}
        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">🎖️ Your Achievements</h3>
            
            {filteredAchievements.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-6xl mb-4">🎯</div>
                <p className="text-gray-600 dark:text-gray-400">No achievements yet. Start creating campaigns to unlock your first achievements!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredAchievements.map((achievement, index) => (
                  <div key={achievement.id || index} className="relative">
                    {/* Timeline line */}
                    {index < filteredAchievements.length - 1 && (
                      <div className="absolute left-6 top-12 w-0.5 h-16 bg-gray-200 dark:bg-gray-600"></div>
                    )}
                    
                    <div className="flex items-start space-x-4">
                      {/* Achievement badge */}
                      <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center text-white text-xl font-bold shadow-lg">
                        {achievement.definition?.badge_icon || '🏆'}
                      </div>
                      
                      {/* Achievement details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {achievement.definition?.name || 'Achievement'}
                          </h4>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {formatTimeAgo(achievement.unlocked_at)}
                          </span>
                        </div>
                        
                        <p className="text-gray-600 dark:text-gray-400 mb-2">
                          {achievement.definition?.description || 'Achievement unlocked!'}
                        </p>
                        
                        <div className="flex items-center space-x-3">
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium border ${getCategoryColor(achievement.definition?.category)}`}>
                            {achievement.definition?.category || 'general'}
                          </span>
                          <span className="text-sm font-semibold text-orange-500">
                            +{achievement.definition?.points || 0} points
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">📈 Recent Activity</h3>
            
            {activities.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-4xl mb-4">📊</div>
                <p className="text-gray-600 dark:text-gray-400 text-sm">No recent activity</p>
              </div>
            ) : (
              <div className="space-y-3">
                {activities.slice(0, 10).map((activity, index) => (
                  <div key={activity.id || index} className="flex items-center space-x-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700">
                    <div className="text-xl">
                      {getActivityIcon(activity.activity_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {activity.activity_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Activity'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {formatTimeAgo(activity.timestamp)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AchievementTimeline;